/**
 * QEVIQ shared Add to Cart — two completely separate purchase flows.
 * Standalone (primary CTA / sticky) never reads bundle selection.
 * Bundle (Upgrade CTA) never submits the standalone product form.
 *
 * Cart replacement: marked solo and bundle purchases in the same
 * _qeviq_purchase_group replace one another without touching unrelated lines.
 */
(function () {
  const SUCCESS_LABEL = '✓ Added to Cart';
  const SUCCESS_MS = 1000;
  const FLOW_STANDALONE = 'standalone';
  const FLOW_BUNDLE = 'bundle';

  const PROP_SYSTEM = '_qeviq_purchase_system';
  const PROP_TYPE = '_qeviq_purchase_type';
  const PROP_GROUP = '_qeviq_purchase_group';
  const PROP_BUNDLE_ID = '_qeviq_bundle_id';
  const PROP_BUNDLE_INSTANCE = '_qeviq_bundle_instance';
  const SYSTEM_VALUE = 'routine_selector';
  const TYPE_SOLO = 'solo';
  const TYPE_BUNDLE = 'bundle';

  const QeviqSharedAddToCart = {
    buttons: new Map(),
    cartLock: false,
    successTimers: new Map(),

    init() {
      this.registerProductPageButtons();
      this.bindVariantSync();
      document.addEventListener('product-info:loaded', () => this.registerProductPageButtons());
    },

    getProductInfo() {
      return document.querySelector('product-info');
    },

    getCartElement() {
      return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    },

    getPrimaryProductForm() {
      return (
        this.getProductInfo()?.querySelector('product-form[data-purchase-flow="standalone"]') ||
        this.getProductInfo()?.querySelector('product-form')
      );
    },

    getStandaloneFormElement() {
      return this.getPrimaryProductForm()?.form || null;
    },

    getSelectedVariantId() {
      const productInfo = this.getProductInfo();
      if (!productInfo) return '';

      const selectedOption = productInfo.querySelector('variant-selects input[data-qv-variant-id]:checked, variant-selects option[data-qv-variant-id]:checked');
      if (selectedOption?.dataset.qvVariantId) {
        if (selectedOption.dataset.qvAvailable === 'false') return '';
        return selectedOption.dataset.qvVariantId;
      }

      const standaloneForm =
        productInfo.querySelector('form[data-purchase-flow="standalone"][data-type="add-to-cart-form"]') ||
        productInfo.querySelector('form[data-type="add-to-cart-form"]');

      const input =
        standaloneForm?.querySelector('input[name="id"]') ||
        productInfo.querySelector('input[name="id"].product-variant-id');

      const formId = String(input?.value || '').trim();
      if (/^\d+$/.test(formId) && Number(formId) > 0) return formId;

      // A variant section refresh can leave the hidden form value empty or invalid.
      // Use Shopify's current selected variant, never a different default colour.
      try {
        const selected = JSON.parse(productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null');
        const selectedId = String(selected?.id || '');
        const urlVariant = new URL(location.href).searchParams.get('variant');
        if (selected?.available && /^\d+$/.test(selectedId) && (!urlVariant || urlVariant === selectedId)) return selectedId;
      } catch (error) { /* Keep the unavailable state if selection cannot be resolved. */ }
      return '';
    },

    getQuantity() {
      const productInfo = this.getProductInfo();
      const quantityInput = productInfo?.querySelector('.quantity__input[name="quantity"]');
      const parsed = parseInt(quantityInput?.value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    },

    getPurchaseGroup() {
      const productInfo = this.getProductInfo();
      const fromAttr = productInfo?.dataset?.qeviqPurchaseGroup?.trim();
      if (fromAttr) return fromAttr.toLowerCase();

      // Fallback when attribute is missing (cached HTML) but this is a QEVIQ PDP.
      if (!productInfo?.classList?.contains('product-info--qeviq')) return '';

      const handle = (productInfo.dataset.url || window.location.pathname || '')
        .split('/')
        .filter(Boolean)
        .pop()
        ?.toLowerCase() || '';

      if (handle.includes('rise')) return 'rise';
      if (handle.includes('reset')) return 'reset';
      if (handle.includes('quiet')) return 'quiet';
      if (handle.includes('drift') || handle.includes('sleep-mask') || handle.includes('sleep_mask')) {
        return 'drift';
      }
      return handle.replace(/^qeviq-/, '').split('-')[0] || '';
    },

    hasRoutineSelector() {
      const productInfo = this.getProductInfo();
      return Boolean(
        this.getPurchaseGroup() || productInfo?.classList?.contains('product-info--qeviq')
      );
    },

    /**
     * Only lines tagged by this routine selector for the active purchase group.
     * Unrelated / unmarked duplicates are intentionally left alone.
     */
    findReplaceableLines(cart, purchaseGroup) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      if (!purchaseGroup) return [];

      const bundleVariantIds = this.getBundleVariantIds();
      const selected = [];
      const seenKeys = new Set();

      items.forEach((item) => {
        if (!item?.key || seenKeys.has(item.key)) return;

        const variantId = String(item.variant_id || item.id || '');
        const isMarked = this.isMarkedRoutineLine(item, purchaseGroup);
        const isKnownBundleVariant = bundleVariantIds.has(variantId);

        if (!isMarked && !isKnownBundleVariant) return;

        seenKeys.add(item.key);
        selected.push(item);
      });

      return selected;
    },

    getBundleVariantIds() {
      const ids = new Set();
      const planRoot = this.getProductInfo()?.querySelector('[data-qbs-plans]');
      const raw = planRoot?.dataset?.bundleVariantIds || '';
      raw.split(',').forEach((id) => {
        const trimmed = id.trim();
        if (trimmed) ids.add(trimmed);
      });
      return ids;
    },

    isMaxQuantityError(responseData) {
      const text = `${responseData?.description || ''} ${responseData?.message || ''}`.toLowerCase();
      return (
        text.includes('maximale hoeveelheid') ||
        text.includes('maximum quantity') ||
        text.includes('already in your cart') ||
        text.includes('zit al in je winkelwagen') ||
        text.includes('cannot be added') ||
        text.includes('not enough inventory')
      );
    },

    getButtonFlow(button) {
      if (!button) return FLOW_STANDALONE;
      if (this.buttons.has(button)) return this.buttons.get(button).flow;
      if (button.matches('[data-qbs-submit], [data-purchase-flow="bundle"]')) return FLOW_BUNDLE;
      return FLOW_STANDALONE;
    },

    getButtonsForFlow(flow) {
      const result = [];
      this.buttons.forEach((meta, button) => {
        if (meta.flow === flow) result.push(button);
      });
      return result;
    },

    getBundleRoot(fromEl) {
      return (
        fromEl?.closest('qeviq-bundle-selector') ||
        fromEl?.closest('form[data-purchase-flow="bundle"]') ||
        this.getProductInfo()?.querySelector('qeviq-bundle-selector') ||
        null
      );
    },

    getSelectedBundleCard(bundleRoot) {
      const root = bundleRoot || this.getBundleRoot();
      if (!root) return null;

      const checkedPlanRadio = root.querySelector('[data-qbs-plan-radio]:checked');
      if (checkedPlanRadio) {
        const planCard = checkedPlanRadio.closest('[data-qbs-plan-card]');
        if (planCard) return planCard;
      }

      const checkedRadio = root.querySelector('[data-qbs-radio]:checked');
      if (checkedRadio) {
        const card = checkedRadio.closest('[data-qbs-card]');
        if (card) return card;
      }

      return (
        root.querySelector('[data-qbs-plan-card].is-selected') ||
        root.querySelector('[data-qbs-card].is-selected') ||
        root.querySelector('[data-qbs-plan-card]') ||
        root.querySelector('[data-qbs-card]')
      );
    },

    getSelectedBundleKey(bundleRoot) {
      const card = this.getSelectedBundleCard(bundleRoot);
      if (!card) return '';

      const explicit = card.getAttribute('data-qeviq-bundle-key')?.trim();
      if (explicit) return explicit;

      const title = card.querySelector('.qbs-card__title')?.textContent?.trim() || '';
      if (title) {
        return title
          .toLowerCase()
          .replace(/\s+bundle\s*$/i, '')
          .trim()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');
      }

      return (card.getAttribute('data-bundle-id') || 'bundle').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
    },

    createBundleInstanceId() {
      const random = Math.random().toString(36).slice(2, 10);
      return `bi_${Date.now().toString(36)}_${random}`;
    },

    buildSoloProperties(purchaseGroup) {
      return {
        [PROP_SYSTEM]: SYSTEM_VALUE,
        [PROP_TYPE]: TYPE_SOLO,
        [PROP_GROUP]: purchaseGroup,
      };
    },

    buildBundleProperties(purchaseGroup, bundleId, bundleInstance) {
      return {
        [PROP_SYSTEM]: SYSTEM_VALUE,
        [PROP_TYPE]: TYPE_BUNDLE,
        [PROP_GROUP]: purchaseGroup,
        [PROP_BUNDLE_ID]: bundleId,
        [PROP_BUNDLE_INSTANCE]: bundleInstance,
      };
    },

    getLineProperty(item, key) {
      const properties = item?.properties;
      if (!properties || typeof properties !== 'object') return '';
      const value = properties[key];
      return value == null ? '' : String(value);
    },

    isMarkedRoutineLine(item, purchaseGroup) {
      if (!item || !purchaseGroup) return false;
      return (
        this.getLineProperty(item, PROP_SYSTEM) === SYSTEM_VALUE &&
        this.getLineProperty(item, PROP_GROUP) === purchaseGroup
      );
    },

    /**
     * Standalone payload only — never reads bundle selection or shared temp arrays.
     */
    resolveStandaloneLineItems(productFormElement) {
      const variantId = this.getSelectedVariantId();
      if (!variantId) return [];
      return [{ id: String(variantId), quantity: 1 }];
    },

    /**
     * Bundle payload only — configured line items from the selected bundle card.
     * Colour sync only when a line item explicitly sets useCurrentVariant.
     */
    resolveBundleLineItems(bundleRoot) {
      const card = this.getSelectedBundleCard(bundleRoot);
      if (!card) return [];

      const variantMapScript = card.querySelector('[data-qbs-bundle-variant-map]');
      if (variantMapScript) {
        try {
          const variantMap = JSON.parse(variantMapScript.textContent || '[]');
          const currentVariantId = String(this.getSelectedVariantId() || '');
          const match = variantMap.find((entry) => String(entry.baseId) === currentVariantId);
          if (match?.bundleId) {
            return [{ id: String(match.bundleId), quantity: 1 }];
          }
        } catch (error) {
          console.error(error);
        }
      }

      const raw = card.getAttribute('data-bundle-line-items');
      if (!raw) {
        const legacyVariantId = card.dataset.variantId;
        if (!legacyVariantId) return [];

        const useCurrentVariant = card.dataset.useCurrentVariant === 'true';
        const variantId = useCurrentVariant ? this.getSelectedVariantId() : legacyVariantId;
        return variantId ? [{ id: String(variantId), quantity: 1 }] : [];
      }

      let items;
      try {
        items = JSON.parse(raw);
      } catch (error) {
        console.error(error);
        return [];
      }

      if (!Array.isArray(items) || !items.length) return [];

      const currentVariantId = this.getSelectedVariantId();

      return items
        .map((item) => {
          const quantity = parseInt(item.quantity, 10) || 1;
          if (item.useCurrentVariant === true && currentVariantId) {
            return { id: String(currentVariantId), quantity };
          }
          if (item.id) {
            return { id: String(item.id), quantity };
          }
          return null;
        })
        .filter(Boolean);
    },

    validateLineItems(lineItems) {
      if (!Array.isArray(lineItems)) return [];

      return lineItems.filter((item) => {
        const id = parseInt(item.id, 10);
        return Number.isFinite(id) && id > 0;
      });
    },

    isStandaloneVariantAvailable(productFormElement) {
      const form = productFormElement?.form || this.getStandaloneFormElement();
      const variantInput = form?.querySelector('input[name="id"]');
      if (variantInput?.disabled) return false;

      const submit = productFormElement?.querySelector('[type="submit"][name="add"]');
      if (submit?.hasAttribute('disabled') || submit?.getAttribute('aria-disabled') === 'true') {
        return false;
      }

      return Boolean(this.getSelectedVariantId());
    },

    appendCartSections(payload, cart) {
      if (!cart?.getSectionsToRender) return payload;

      const sections = cart.getSectionsToRender().map((section) => section.id);
      const next = { ...payload, sections, sections_url: window.location.pathname };
      cart.setActiveElement(document.activeElement);
      return next;
    },

    appendCartSectionsToFormData(formData, cart) {
      if (!cart?.getSectionsToRender) return;
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    },

    hasRenderableCartSections(responseData, cart) {
      if (!cart?.getSectionsToRender || !responseData?.sections) return false;
      return cart.getSectionsToRender().every((section) => {
        const html = responseData.sections[section.id];
        return typeof html === 'string' && html.length > 0;
      });
    },

    async fetchMissingCartSections(responseData, cart) {
      if (!cart?.getSectionsToRender) return responseData;

      const sections = { ...(responseData.sections || {}) };
      const missingSectionIds = cart
        .getSectionsToRender()
        .map((section) => section.id)
        .filter((sectionId) => !(typeof sections[sectionId] === 'string' && sections[sectionId].length > 0));

      if (!missingSectionIds.length) {
        responseData.sections = sections;
        return responseData;
      }

      const sectionResponse = await fetch(
        `${window.location.pathname}?sections=${missingSectionIds.join(',')}`
      );
      const sectionData = await sectionResponse.json();

      missingSectionIds.forEach((sectionId) => {
        if (typeof sectionData[sectionId] === 'string' && sectionData[sectionId].length > 0) {
          sections[sectionId] = sectionData[sectionId];
        }
      });

      responseData.sections = sections;
      return responseData;
    },

    async fetchCart() {
      const response = await fetch(`${routes.cart_url}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        throw new Error(window.cartStrings?.error || 'Unable to read cart.');
      }
      return response.json();
    },

    snapshotLinesForRestore(lines) {
      return lines.map((item) => ({
        id: item.id || item.variant_id,
        quantity: item.quantity || 1,
        properties: { ...(item.properties || {}) },
      }));
    },

    async removeMarkedLines(lines, { includeSections = false, cart = null } = {}) {
      if (!lines.length) return { ok: true, data: null };

      // Remove by line-item key so unmarked copies of the same variant stay untouched.
      // Prefer a single /cart/update.js call so multi-line bundle instances clear atomically.
      const updates = {};
      lines.forEach((item) => {
        if (item?.key) updates[item.key] = 0;
      });

      const keys = Object.keys(updates);
      if (!keys.length) return { ok: true, data: null };

      let updateBody = { updates };
      if (includeSections && cart) {
        updateBody = this.appendCartSections(updateBody, cart);
      }

      const updateConfig = fetchConfig('javascript');
      updateConfig.body = JSON.stringify(updateBody);

      const updateUrl = routes.cart_update_url || `${routes.cart_url}/update.js`;
      const response = await fetch(updateUrl, updateConfig);
      const data = await response.json();

      if (data.errors || data.status) {
        const errorMessage =
          (typeof data.errors === 'string' && data.errors) ||
          data.description ||
          data.message ||
          window.cartStrings?.error ||
          'Unable to update cart.';
        return { ok: false, errorMessage, data };
      }

      return { ok: true, data };
    },

    async postCartAddItems(items, { includeSections = false, cart = null } = {}) {
      let body = { items };
      if (includeSections && cart) {
        body = this.appendCartSections(body, cart);
      }

      const config = fetchConfig('javascript');
      config.body = JSON.stringify(body);

      const response = await fetch(`${routes.cart_add_url}`, config);
      return response.json();
    },

    async postCartAddFormData(formData) {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      return response.json();
    },

    appendPropertiesToFormData(formData, properties) {
      Object.entries(properties || {}).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, String(value));
      });
    },

    registerButton(button, flow = FLOW_STANDALONE) {
      if (!button) return;
      this.buttons.set(button, { flow });
      button.setAttribute('data-qeviq-shared-atc', 'true');
      button.setAttribute('data-purchase-flow', flow);

      const label = button.querySelector('[data-qeviq-atc-label]') || this.getButtonLabelElement(button);
      if (label && !label.dataset.qeviqAtcDefaultLabel) {
        label.dataset.qeviqAtcDefaultLabel = label.textContent.trim();
      }
    },

    registerProductPageButtons() {
      const productInfo = this.getProductInfo();
      if (!productInfo) return;

      productInfo.querySelectorAll('product-form [type="submit"][name="add"]').forEach((button) => {
        this.registerButton(button, FLOW_STANDALONE);
      });

      productInfo.querySelectorAll('[data-qbs-submit]').forEach((button) => {
        this.registerButton(button, FLOW_BUNDLE);
      });

      const stickySubmit = productInfo.closest('.shopify-section')?.querySelector('[data-qeviq-sticky-submit]');
      if (stickySubmit && productInfo.querySelector('[data-qbs-plans]')) {
        this.registerButton(stickySubmit, FLOW_BUNDLE);
      }
    },

    getButtonLabelElement(button) {
      return (
        button.querySelector('[data-qeviq-atc-label]') ||
        button.querySelector('.qbs__submit-text') ||
        button.querySelector('span:not(.loading__spinner):not(.sold-out-message):not(.qbs__submit-loading)')
      );
    },

    bindVariantSync() {
      if (typeof subscribe !== 'function' || !window.PUB_SUB_EVENTS?.variantChange) return;

      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        window.requestAnimationFrame(() => this.syncStandaloneAvailability());
      });
    },

    syncStandaloneAvailability() {
      const primarySubmit = this.getPrimaryProductForm()?.querySelector('[type="submit"][name="add"]');
      if (!primarySubmit) return;

      const isDisabled =
        primarySubmit.hasAttribute('disabled') || primarySubmit.getAttribute('aria-disabled') === 'true';

      this.getButtonsForFlow(FLOW_STANDALONE).forEach((button) => {
        if (button === primarySubmit) return;
        if (this.cartLock && this.getButtonFlow(button) === FLOW_STANDALONE) return;

        if (isDisabled) {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
        } else if (!this.cartLock) {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
        }
      });
    },

    setFlowLoading(flow, isLoading) {
      this.getButtonsForFlow(flow).forEach((button) => {
        button.classList.toggle('loading', isLoading);
        button.classList.toggle('is-loading', isLoading);

        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);

        const bundleSpinner = button.querySelector('.qbs__submit-loading');
        if (bundleSpinner) bundleSpinner.classList.toggle('hidden', !isLoading);

        if (isLoading) {
          button.setAttribute('aria-disabled', 'true');
          button.disabled = true;
        } else {
          button.removeAttribute('aria-disabled');
          button.disabled = false;
        }
      });

      if (flow === FLOW_STANDALONE && !isLoading) {
        this.syncStandaloneAvailability();
      }
    },

    showFlowSuccess(flow) {
      const existing = this.successTimers.get(flow);
      if (existing) window.clearTimeout(existing);

      this.getButtonsForFlow(flow).forEach((button) => {
        const label = this.getButtonLabelElement(button);
        if (!label) return;
        if (!label.dataset.qeviqAtcDefaultLabel) {
          label.dataset.qeviqAtcDefaultLabel = label.textContent.trim();
        }
        label.textContent = SUCCESS_LABEL;
      });

      return new Promise((resolve) => {
        const timer = window.setTimeout(() => {
          this.restoreFlowLabels(flow);
          this.successTimers.delete(flow);
          resolve();
        }, SUCCESS_MS);
        this.successTimers.set(flow, timer);
      });
    },

    restoreFlowLabels(flow) {
      this.getButtonsForFlow(flow).forEach((button) => {
        const label = this.getButtonLabelElement(button);
        if (!label) return;

        const productForm = button.closest('product-form');
        const customLabel = productForm?.dataset.addToCartLabel;
        const defaultLabel = label.dataset.qeviqAtcDefaultLabel;

        if (customLabel) {
          label.textContent = customLabel;
        } else if (defaultLabel) {
          label.textContent = defaultLabel;
        } else if (window.variantStrings?.addToCart) {
          label.textContent = window.variantStrings.addToCart;
        }
      });
    },

    showProductFormError(productFormElement, message) {
      if (!productFormElement || productFormElement.dataset.hideErrors === 'true') return;
      productFormElement.handleErrorMessage(message);
    },

    clearProductFormError(productFormElement) {
      productFormElement?.handleErrorMessage();
    },

    showBundleError(bundleRoot, message) {
      const errorEl =
        bundleRoot?.querySelector('[data-qbs-error]') ||
        this.getProductInfo()?.querySelector('[data-qbs-error]');
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
    },

    clearBundleError(bundleRoot) {
      const errorEl =
        bundleRoot?.querySelector('[data-qbs-error]') ||
        this.getProductInfo()?.querySelector('[data-qbs-error]');
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.classList.add('hidden');
    },

    createCartLinesUpdateEvent(productFormElement, variantId, quantity) {
      if (!productFormElement?.createCartLinesUpdateEvent) return null;
      return productFormElement.createCartLinesUpdateEvent(variantId, quantity);
    },

    resolveCartLinesUpdate(productFormElement, deferred) {
      if (!productFormElement?.resolveCartLinesUpdate) return;
      productFormElement.resolveCartLinesUpdate(deferred);
    },

    dispatchCartError(productFormElement, message, code = 'INVALID') {
      if (productFormElement?.dispatchCartErrorEvent) {
        productFormElement.dispatchCartErrorEvent(message, code);
      }
    },

    async refreshCartUi(responseData, cart) {
      if (!cart) {
        window.location = window.routes.cart_url;
        return { redirected: true };
      }

      let data = responseData || {};
      if (!this.hasRenderableCartSections(data, cart)) {
        data = await this.fetchMissingCartSections(data, cart);
      }

      if (!this.hasRenderableCartSections(data, cart)) {
        const sectionIds = cart.getSectionsToRender().map((section) => section.id);
        const sectionResponse = await fetch(
          `${window.location.pathname}?sections=${sectionIds.join(',')}`
        );
        const sectionData = await sectionResponse.json();
        data = { ...data, sections: sectionData };
      }

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        const startMarker =
          typeof CartPerformance !== 'undefined'
            ? CartPerformance.createStartingMarker('add:wait-for-subscribers')
            : null;

        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'qeviq-shared-add-to-cart',
          cartData: data,
        });

        if (startMarker && typeof CartPerformance !== 'undefined') {
          CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
        }
      }

      if (this.hasRenderableCartSections(data, cart)) {
        if (typeof CartPerformance !== 'undefined') {
          CartPerformance.measure('add:paint-updated-sections', () => {
            cart.renderContents(data);
          });
        } else {
          cart.renderContents(data);
        }

        if (cart.classList?.contains('is-empty')) cart.classList.remove('is-empty');
      }

      if (typeof cart.open === 'function') {
        cart.open(document.activeElement);
      }

      return { redirected: false, data };
    },

    async restorePreviousPurchase(snapshotItems) {
      if (!Array.isArray(snapshotItems) || !snapshotItems.length) return { ok: false };

      try {
        const data = await this.postCartAddItems(snapshotItems, { includeSections: false });
        if (data?.status) return { ok: false, data };
        return { ok: true, data };
      } catch (error) {
        console.error(error);
        return { ok: false, error };
      }
    },

    /**
     * Atomic replace: validate → remove marked group lines → add new purchase → refresh once.
     */
    async replacePurchase({
      purchaseType,
      lineItems,
      propertiesForItem,
      productFormElement,
      source,
      flow,
      evt,
      bundleRoot,
    }) {
      const cart = this.getCartElement();
      const purchaseGroup = this.getPurchaseGroup() || 'routine';
      const validItems = this.validateLineItems(lineItems);

      if (!validItems.length) {
        const errorMessage = window.variantStrings?.unavailable || 'Please select an available option.';
        if (flow === FLOW_BUNDLE) this.showBundleError(bundleRoot, errorMessage);
        else this.showProductFormError(productFormElement, errorMessage);
        this.dispatchCartError(productFormElement, errorMessage, 'INVALID');
        return { ok: false };
      }

      const primaryVariantId = validItems[0].id;
      const primaryQuantity = validItems[0].quantity || 1;
      const linesUpdateDeferred =
        flow === FLOW_STANDALONE
          ? this.createCartLinesUpdateEvent(productFormElement, primaryVariantId, primaryQuantity)
          : null;

      let removedSnapshot = [];
      let didRemove = false;

      try {
        const currentCart = await this.fetchCart();
        const replaceableLines = this.findReplaceableLines(currentCart, purchaseGroup);
        removedSnapshot = this.snapshotLinesForRestore(replaceableLines);

        if (replaceableLines.length) {
          const removeResult = await this.removeMarkedLines(replaceableLines, {
            includeSections: false,
            cart,
          });

          if (!removeResult.ok) {
            if (flow === FLOW_BUNDLE) this.showBundleError(bundleRoot, removeResult.errorMessage);
            else this.showProductFormError(productFormElement, removeResult.errorMessage);
            this.dispatchCartError(productFormElement, removeResult.errorMessage, 'INVALID');
            linesUpdateDeferred?.reject(new Error(removeResult.errorMessage));
            return { ok: false };
          }

          didRemove = true;
        }

        const itemsToAdd = validItems.map((item, index) => ({
          id: Number(item.id),
          quantity: item.quantity || 1,
          properties: propertiesForItem(item, index),
        }));

        let responseData = await this.postCartAddItems(itemsToAdd, {
          includeSections: true,
          cart,
        });

        // Marked lines can briefly remain if another tab raced; clear once more and retry.
        // Never remove unmarked / unrelated lines to work around inventory errors.
        if (responseData?.status && this.isMaxQuantityError(responseData)) {
          const cartAfterFail = await this.fetchCart();
          const retryLines = this.findReplaceableLines(cartAfterFail, purchaseGroup);
          if (retryLines.length) {
            const retryRemove = await this.removeMarkedLines(retryLines, {
              includeSections: false,
              cart,
            });
            if (retryRemove.ok) {
              didRemove = true;
              removedSnapshot = removedSnapshot.concat(this.snapshotLinesForRestore(retryLines));
              responseData = await this.postCartAddItems(itemsToAdd, {
                includeSections: true,
                cart,
              });
            }
          }
        }

        if (!responseData) {
          throw new Error(window.cartStrings?.error || 'Unable to add to cart.');
        }

        if (responseData.status) {
          const errorMessage =
            responseData.description || responseData.message || window.cartStrings?.error;

          if (didRemove && removedSnapshot.length) {
            await this.restorePreviousPurchase(removedSnapshot);
          }

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartError) {
            publish(PUB_SUB_EVENTS.cartError, {
              source,
              productVariantId: primaryVariantId,
              errors: responseData.errors || responseData.description,
              message: responseData.message,
            });
          }

          if (flow === FLOW_BUNDLE) this.showBundleError(bundleRoot, errorMessage);
          else this.showProductFormError(productFormElement, errorMessage);
          this.dispatchCartError(productFormElement, errorMessage, 'INVALID');
          linesUpdateDeferred?.reject(new Error(errorMessage));

          try {
            await this.refreshCartUi(null, cart);
          } catch (refreshError) {
            console.error(refreshError);
          }

          return { ok: false };
        }

        if (flow === FLOW_STANDALONE) this.clearProductFormError(productFormElement);
        else this.clearBundleError(bundleRoot);

        this.resolveCartLinesUpdate(productFormElement, linesUpdateDeferred);

        const uiResult = await this.refreshCartUi(responseData, cart);
        if (uiResult.redirected) return { ok: true, redirected: true };

        await this.showFlowSuccess(flow);

        if (evt && typeof CartPerformance !== 'undefined') {
          CartPerformance.measureFromEvent('add:user-action', evt);
        }

        return { ok: true };
      } catch (error) {
        console.error(error);

        if (didRemove && removedSnapshot.length) {
          await this.restorePreviousPurchase(removedSnapshot);
          try {
            await this.refreshCartUi(null, cart);
          } catch (refreshError) {
            console.error(refreshError);
          }
        }

        const errorMessage = error.message || window.cartStrings?.error || 'Something went wrong. Please try again.';
        if (flow === FLOW_BUNDLE) this.showBundleError(bundleRoot, errorMessage);
        else this.showProductFormError(productFormElement, errorMessage);
        this.dispatchCartError(productFormElement, errorMessage, 'SERVICE_UNAVAILABLE');
        linesUpdateDeferred?.reject(error);
        return { ok: false };
      }
    },

    /**
     * Legacy single/multi add without replacement — used outside QEVIQ routine pages.
     */
    async addLineItemsLegacy(lineItems, { productFormElement, source, flow, evt, bundleRoot }) {
      const cart = this.getCartElement();
      const validItems = this.validateLineItems(lineItems);

      if (!validItems.length) {
        const errorMessage = window.variantStrings?.unavailable || 'Please select an available option.';
        if (flow === FLOW_BUNDLE) this.showBundleError(bundleRoot, errorMessage);
        else this.showProductFormError(productFormElement, errorMessage);
        this.dispatchCartError(productFormElement, errorMessage, 'INVALID');
        return { ok: false };
      }

      const primaryVariantId = validItems[0].id;
      const primaryQuantity = validItems[0].quantity || 1;
      const linesUpdateDeferred =
        flow === FLOW_STANDALONE
          ? this.createCartLinesUpdateEvent(productFormElement, primaryVariantId, primaryQuantity)
          : null;

      try {
        const itemsToAdd = validItems.map((item) => ({
          id: Number(item.id),
          quantity: item.quantity || 1,
        }));

        let responseData = await this.postCartAddItems(itemsToAdd, {
          includeSections: true,
          cart,
        });

        if (!responseData) {
          throw new Error(window.cartStrings?.error || 'Unable to add to cart.');
        }

        if (responseData.status) {
          const errorMessage = responseData.description || responseData.message || window.cartStrings?.error;
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartError) {
            publish(PUB_SUB_EVENTS.cartError, {
              source,
              productVariantId: primaryVariantId,
              errors: responseData.errors || responseData.description,
              message: responseData.message,
            });
          }
          if (flow === FLOW_BUNDLE) this.showBundleError(bundleRoot, errorMessage);
          else this.showProductFormError(productFormElement, errorMessage);
          this.dispatchCartError(productFormElement, errorMessage, 'INVALID');
          linesUpdateDeferred?.reject(new Error(errorMessage));
          return { ok: false };
        }

        if (flow === FLOW_STANDALONE) this.clearProductFormError(productFormElement);
        else this.clearBundleError(bundleRoot);
        this.resolveCartLinesUpdate(productFormElement, linesUpdateDeferred);

        const uiResult = await this.refreshCartUi(responseData, cart);
        if (uiResult.redirected) return { ok: true, redirected: true };

        await this.showFlowSuccess(flow);
        if (evt && typeof CartPerformance !== 'undefined') {
          CartPerformance.measureFromEvent('add:user-action', evt);
        }
        return { ok: true };
      } catch (error) {
        console.error(error);
        const errorMessage = error.message || window.cartStrings?.error || 'Something went wrong. Please try again.';
        if (flow === FLOW_BUNDLE) this.showBundleError(bundleRoot, errorMessage);
        else this.showProductFormError(productFormElement, errorMessage);
        this.dispatchCartError(productFormElement, errorMessage, 'SERVICE_UNAVAILABLE');
        linesUpdateDeferred?.reject(error);
        return { ok: false };
      }
    },

    async runFlow({ flow, purchaseType, lineItems, propertiesForItem, productFormElement, source, evt, bundleRoot }) {
      if (this.cartLock) return { ok: false, locked: true };

      if (purchaseType === TYPE_SOLO && !this.isStandaloneVariantAvailable(productFormElement)) {
        const errorMessage = window.variantStrings?.unavailable || 'Please select an available option.';
        this.showProductFormError(productFormElement, errorMessage);
        this.dispatchCartError(productFormElement, errorMessage, 'INVALID');
        return { ok: false };
      }

      this.cartLock = true;
      this.setFlowLoading(flow, true);

      try {
        if (!this.hasRoutineSelector() || typeof propertiesForItem !== 'function') {
          return await this.addLineItemsLegacy(lineItems, {
            productFormElement,
            source,
            flow,
            evt,
            bundleRoot,
          });
        }

        return await this.replacePurchase({
          purchaseType,
          lineItems,
          propertiesForItem,
          productFormElement,
          source,
          flow,
          evt,
          bundleRoot,
        });
      } finally {
        this.cartLock = false;
        this.setFlowLoading(flow, false);
      }
    },

    async handleProductFormSubmit(productFormElement, evt) {
      if (productFormElement.submitButton?.getAttribute('aria-disabled') === 'true') return;

      this.clearProductFormError(productFormElement);
      const purchaseGroup = this.getPurchaseGroup() || 'routine';
      const lineItems = this.resolveStandaloneLineItems(productFormElement);
      if (!lineItems.length) {
        this.showProductFormError(productFormElement, window.variantStrings?.unavailable);
        return;
      }

      this.clearProductFormError(productFormElement);
      const soloProperties = this.buildSoloProperties(purchaseGroup);

      await this.runFlow({
        flow: FLOW_STANDALONE,
        purchaseType: TYPE_SOLO,
        lineItems,
        propertiesForItem: () => ({ ...soloProperties }),
        productFormElement,
        source: 'product-form',
        evt,
      });
    },

    async handleStandaloneClick(button, evt) {
      evt?.preventDefault();
      if (!button || button.getAttribute('aria-disabled') === 'true' || button.hasAttribute('disabled')) return;

      const productFormElement = this.getPrimaryProductForm();
      const purchaseGroup = this.getPurchaseGroup();
      const lineItems = this.resolveStandaloneLineItems(productFormElement);
      if (!lineItems.length) {
        this.showProductFormError(productFormElement, window.variantStrings?.unavailable);
        return;
      }

      const soloProperties = this.buildSoloProperties(purchaseGroup);

      await this.runFlow({
        flow: FLOW_STANDALONE,
        purchaseType: TYPE_SOLO,
        lineItems,
        propertiesForItem: () => ({ ...soloProperties }),
        productFormElement,
        source: 'product-form',
        evt,
      });
    },

    async handleBundleSubmit(button, evt) {
      evt?.preventDefault();
      if (!button || button.getAttribute('aria-disabled') === 'true' || button.hasAttribute('disabled')) return;

      const bundleRoot = this.getBundleRoot(button);
      this.clearBundleError(bundleRoot);

      const purchaseGroup = this.getPurchaseGroup() || 'routine';
      const lineItems = this.resolveBundleLineItems(bundleRoot);

      if (!lineItems.length) {
        this.showBundleError(bundleRoot, 'This bundle is not linked to a product yet.');
        return;
      }

      const bundleId = this.getSelectedBundleKey(bundleRoot) || 'bundle';
      const bundleInstance = this.createBundleInstanceId();
      const bundleProperties = this.buildBundleProperties(purchaseGroup, bundleId, bundleInstance);

      await this.runFlow({
        flow: FLOW_BUNDLE,
        purchaseType: TYPE_BUNDLE,
        lineItems,
        propertiesForItem: () => ({ ...bundleProperties }),
        productFormElement: this.getPrimaryProductForm(),
        source: 'qeviq-bundle-selector',
        evt,
        bundleRoot,
      });
    },

    /** @deprecated Prefer handleStandaloneClick / handleBundleSubmit */
    async handleButtonClick(button, evt) {
      const flow = this.getButtonFlow(button);
      if (flow === FLOW_BUNDLE) {
        return this.handleBundleSubmit(button, evt);
      }
      return this.handleStandaloneClick(button, evt);
    },

    // Back-compat aliases used by older sticky / bundle wiring
    syncButtonStates() {
      this.syncStandaloneAvailability();
    },
  };

  window.QeviqSharedAddToCart = QeviqSharedAddToCart;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => QeviqSharedAddToCart.init());
  } else {
    QeviqSharedAddToCart.init();
  }
})();
